import { useCallback, useState } from 'react';
import { useRelayEnvironment, fetchQuery } from 'react-relay';
import { GraphQLTaggedNode, getRequest, createOperationDescriptor } from 'relay-runtime';

type QueryShape = {
    readonly response: Record<string, unknown>;
    readonly variables: Record<string, unknown>;
};
type LoadingState = 'done' | 'loading' | 'error';

export function useRendererQueryLoader<T extends QueryShape>(
    request: GraphQLTaggedNode
): [(variables: T['variables']) => void, T['response'] | null, LoadingState, unknown] {
    const environment = useRelayEnvironment();
    const [loadingState, setLoadingState] = useState<LoadingState>('done');
    const [queryResult, setQueryResult] = useState<T['response'] | null>(null);
    const [queryError, setQueryError] = useState<unknown | null>(null);
    const refetchCallback = useCallback(
        (variables: T['variables']) => {
            // variables for network fetch
            const queryDescriptor = createOperationDescriptor(getRequest(request), variables);
            environment.retain(queryDescriptor);
            return fetchQuery(environment, request, variables).subscribe({
                complete: () => {
                    setLoadingState('done');
                },
                start: () => {
                    setLoadingState('loading');
                },
                error: (error: unknown) => {
                    setLoadingState('error');
                    setQueryResult(null);
                    setQueryError(error);
                },
                next: data => {
                    setLoadingState('done');
                    setQueryResult(data);
                },
            });
        },
        [environment, request]
    );
    return [refetchCallback, queryResult, loadingState, queryError];
}
